// externals
import type { Reducer } from 'redux';

// libraries
import type { PurchaseHistoryState } from '@makemydeal/dr-dash-types';
import { actions as offerReduxActionTypes } from '@makemydeal/dr-offer-redux';

// consts/enums
import {
    PURCHASE_HISTORY_EVENT_RECEIVED,
    PURCHASE_HISTORY_NOTIFICATION_ACTIVATED,
    PURCHASE_HISTORY_NOTIFICATION_DISMISSED,
    PURCHASE_HISTORY_NOTIFICATION_RAISED,
    PURCHASE_HISTORY_REQUESTED,
    PURCHASE_HISTORY_REQUEST_ACCEPTED,
    PURCHASE_HISTORY_REQUEST_FAILED,
    PURCHASE_HISTORY_REQUEST_UNAVAILABLE,
    PURCHASE_HISTORY_TAKING_LONGER,
    PURCHASE_HISTORY_TIMED_OUT
} from '../actionTypes/purchaseHistoryActionTypes';

export type PurchaseHistoryReducer = Reducer<PurchaseHistoryState>;

/** `unavailable`, not `loading`: the section must not show a skeleton before anything was asked for. */
export const initialState: PurchaseHistoryState = {
    status: 'unavailable',
    records: [],
    isTakingLonger: false,
    isRequestOutstanding: false,
    requestedAtMs: null,
    correlationId: null,
    notification: {
        isRaised: false,
        uniqueScrollRequestId: false
    }
};

/** Every resolution path clears the outstanding flag and the slow hint. */
const resolved = (state: PurchaseHistoryState) => ({
    ...state,
    isRequestOutstanding: false,
    isTakingLonger: false
});

export const reducer: PurchaseHistoryReducer = (state: PurchaseHistoryState = initialState, action: any) => {
    switch (action.type) {
        case PURCHASE_HISTORY_REQUESTED: {
            // Clearing records here is what makes "most recent answer wins" fall out of one flag.
            return {
                ...state,
                status: 'loading' as const,
                records: [],
                isTakingLonger: false,
                isRequestOutstanding: true,
                // Anchored to the FIRST request: a re-open must not restart the clocks.
                requestedAtMs: state.isRequestOutstanding ? state.requestedAtMs : action.payload.requestedAtMs
            };
        }

        case PURCHASE_HISTORY_REQUEST_ACCEPTED: {
            // Still loading: the acknowledgement is not the answer.
            return { ...state, correlationId: action.payload.correlationId };
        }

        case PURCHASE_HISTORY_REQUEST_UNAVAILABLE:
        case PURCHASE_HISTORY_REQUEST_FAILED: {
            // No push will arrive, so resolve now rather than waiting out the bound.
            return { ...resolved(state), status: 'unavailable' as const, records: [] };
        }

        case PURCHASE_HISTORY_TAKING_LONGER: {
            if (!state.isRequestOutstanding) {
                return state;
            }
            return { ...state, isTakingLonger: true };
        }

        case PURCHASE_HISTORY_TIMED_OUT: {
            if (!state.isRequestOutstanding) {
                return state;
            }
            return { ...resolved(state), status: 'unavailable' as const, records: [] };
        }

        case PURCHASE_HISTORY_EVENT_RECEIVED: {
            const { state: pushState, records } = action.payload;

            if (pushState === 'error') {
                // Resolve immediately rather than waiting out the bound.
                return { ...resolved(state), status: 'unavailable' as const, records: [] };
            }

            const received = Array.isArray(records) ? records : [];

            // Renderability is the selector's call, so a zero-length payload is all the reducer can judge.
            if (pushState === 'empty' || received.length === 0) {
                return { ...resolved(state), status: 'empty' as const, records: [] };
            }

            return {
                ...resolved(state),
                status: 'ready' as const,
                // Replaced, never appended to.
                records: received
            };
        }

        case PURCHASE_HISTORY_NOTIFICATION_RAISED: {
            return { ...state, notification: { ...state.notification, isRaised: true } };
        }

        case PURCHASE_HISTORY_NOTIFICATION_ACTIVATED: {
            return {
                ...state,
                notification: { isRaised: false, uniqueScrollRequestId: action.payload.uniqueScrollRequestId }
            };
        }

        case PURCHASE_HISTORY_NOTIFICATION_DISMISSED: {
            return { ...state, notification: { ...state.notification, isRaised: false } };
        }

        case offerReduxActionTypes.SWAPPED_GLOBAL_CUSTOMER_IDS: {
            return initialState;
        }

        default: {
            return state;
        }
    }
};
